import {
  Box,
  Checkbox,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextareaAutosize,
  Typography,
} from '@material-ui/core';
import React, { useEffect } from 'react';

import {
  ErrorDisplay,
  LoadingIndicator,
  SubmitButton,
  useCommonStyle,
  useObservable,
} from '@lib/common';
import { FormStatus, guestSurveyQuery } from '@lib/state';
import { useFormContext } from 'react-hook-form';

export interface GuestSurveyFormFields {
  facilitiesRating?: number;
  staffRating?: number;
  foodRating?: number;
  comments?: string;
  makeTicket?: boolean;
}

interface Props {
  status?: FormStatus;
  error?: Error;
}

export const GuestSurveyForm: React.FC<Props> = ({ status, error }) => {
  const styles = useCommonStyle();
  const { register, watch, setValue } = useFormContext();
  const guestSurvey = useObservable(guestSurveyQuery.guestSurvey);

  useEffect(() => {
    register({ name: 'facilitiesRating' });
    register({ name: 'staffRating' });
    register({ name: 'foodRating' });
    register({ name: 'comments' });
    register({ name: 'makeTicket' });
  }, [register, setValue]);

  // non-undefined default value is expected for a uncontrolled radiobutton element
  const facilitiesRating = watch('facilitiesRating', 0);
  const staffRating = watch('staffRating', 0);
  const foodRating = watch('foodRating', 0);
  const comments = watch('comments');
  const makeTicket = watch('makeTicket', false);

  useEffect(() => {
    if (!comments) {
      setValue('makeTicket', false);
    }
  }, [comments, setValue]);

  return (
    <>
      {status === FormStatus.Success ? (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h5" align="center" color="textSecondary" paragraph>
              Feedback Submitted
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1" align="center">
              Thank you, your feedback has been received. A manager will review.
            </Typography>
          </Grid>
          {guestSurvey?.maintenanceTicketNumber && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" align="center" color="textSecondary" paragraph>
                  Ticket #{guestSurvey.maintenanceTicketNumber}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" align="center">
                  Your ticket has been submitted. This will be automatically routed to our
                  maintenance staff.
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
      ) : (
        <>
          <Box display="flex" alignItems="center" justifyContent="space-between" pb={2}>
            <Typography className={styles.dense} variant="subtitle2">
              Thank you for staying with us, please take a few minutes to complete the following
              guest survey, so we can serve you better.
            </Typography>
          </Box>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12}>
              <FormLabel>Facilities</FormLabel>
              <RadioGroup
                row
                value={facilitiesRating}
                onChange={e => setValue('facilitiesRating', Number(e.target.value))}
                name="facilitiesRating"
              >
                <FormControlLabel value={1} label="1-Poor" control={<Radio />} />
                <FormControlLabel value={2} label="2" control={<Radio />} />
                <FormControlLabel value={3} label="3" control={<Radio />} />
                <FormControlLabel value={4} label="4" control={<Radio />} />
                <FormControlLabel value={5} label="5-Good" control={<Radio />} />
              </RadioGroup>
            </Grid>
            <Grid item xs={12}>
              <FormLabel>Staff</FormLabel>
              <RadioGroup
                row
                value={staffRating}
                onChange={e => setValue('staffRating', Number(e.target.value))}
                name="staffRating"
              >
                <FormControlLabel value={1} label="1-Poor" control={<Radio />} />
                <FormControlLabel value={2} label="2" control={<Radio />} />
                <FormControlLabel value={3} label="3" control={<Radio />} />
                <FormControlLabel value={4} label="4" control={<Radio />} />
                <FormControlLabel value={5} label="5-Good" control={<Radio />} />
              </RadioGroup>
            </Grid>
            <Grid item xs={12}>
              <FormLabel>Food</FormLabel>
              <RadioGroup
                row
                value={foodRating}
                onChange={e => setValue('foodRating', Number(e.target.value))}
                name="foodRating"
              >
                <FormControlLabel value={1} label="1-Poor" control={<Radio />} />
                <FormControlLabel value={2} label="2" control={<Radio />} />
                <FormControlLabel value={3} label="3" control={<Radio />} />
                <FormControlLabel value={4} label="4" control={<Radio />} />
                <FormControlLabel value={5} label="5-Good" control={<Radio />} />
              </RadioGroup>
            </Grid>
            <Grid item xs={12}>
              <FormLabel>Comments</FormLabel>
              <TextareaAutosize
                name="comments"
                style={{ width: '100%' }}
                minRows={5}
                maxRows={20}
                placeholder="Please provide optional comments here."
                value={comments}
                onChange={e => setValue('comments', e.target.value)}
              />
              {comments && (
                <>
                  <FormLabel>
                    Would you like to open a maintenance ticket for this comment?
                  </FormLabel>
                  <Checkbox
                    name="makeTicket"
                    value={makeTicket}
                    onChange={_ => setValue('makeTicket', !makeTicket)}
                  />
                </>
              )}
            </Grid>
          </Grid>
          <ErrorDisplay error={error} />
          {status === FormStatus.Pending ? <LoadingIndicator loadingText="Processing..." /> : null}
          <Box display="flex" justifyContent="flex-end" pb={2}>
            <SubmitButton
              name="submitSurvey"
              variant="contained"
              color="secondary"
              fullWidth
              pending={status === FormStatus.Pending}
              disabled={status === FormStatus.Pending}
            >
              Submit Survey
            </SubmitButton>
          </Box>
        </>
      )}
    </>
  );
};
